/**
 * Logger Utility
 *
 * Environment-aware logging system that replaces console.log throughout the application.
 * Automatically respects NODE_ENV and provides structured logging with levels.
 *
 * Usage:
 *   import logger from '@/utils/logger'
 *
 *   logger.debug('Detailed debug info', { data })
 *   logger.info('Informational message', context)
 *   logger.warn('Warning message', details)
 *   logger.error('Error occurred', error)
 *
 * Features:
 * - Automatic production silencing (debug/info disabled)
 * - Structured log formatting with timestamps
 * - Color-coded console output in development
 * - Error stack trace preservation
 * - Context data support (objects, arrays)
 * - Performance-friendly (no-op in production for debug/info)
 */

class Logger {
  constructor() {
    // Determine environment
    this.isDevelopment = process.env.NODE_ENV === 'development'
    this.isProduction = process.env.NODE_ENV === 'production'

    // Log levels (ordered by severity)
    this.levels = {
      DEBUG: 0,
      INFO: 1,
      WARN: 2,
      ERROR: 3,
      NONE: 4
    }

    // Set current log level based on environment
    // Production: Only WARN and ERROR
    // Development: All levels
    this.currentLevel = this.isProduction
      ? this.levels.WARN
      : this.levels.DEBUG

    // Allow override via environment variable
    const envLevel = process.env.VUE_APP_LOG_LEVEL
    if (envLevel && this.levels[envLevel.toUpperCase()] !== undefined) {
      this.currentLevel = this.levels[envLevel.toUpperCase()]
    }

    // Console styling for development
    this.styles = {
      DEBUG: 'color: #888; font-weight: normal',
      INFO: 'color: #2196F3; font-weight: bold',
      WARN: 'color: #FF9800; font-weight: bold',
      ERROR: 'color: #F44336; font-weight: bold'
    }

    // Emoji indicators for quick visual scanning
    this.indicators = {
      DEBUG: '🔍',
      INFO: 'ℹ️',
      WARN: '⚠️',
      ERROR: '❌'
    }
  }

  /**
   * Check if a log level should be output
   * @private
   */
  _shouldLog(level) {
    return this.levels[level] >= this.currentLevel
  }

  /**
   * Format timestamp for log entry
   * @private
   */
  _getTimestamp() {
    const now = new Date()
    return now.toISOString().split('T')[1].split('.')[0] // HH:MM:SS
  }

  /**
   * Format log message with level, timestamp, and styling
   * @private
   */
  _formatMessage(level, message, context) {
    const timestamp = this._getTimestamp()
    const indicator = this.indicators[level]

    if (this.isDevelopment) {
      // Development: Styled console output
      return [
        `%c${indicator} [${level}] ${timestamp}`,
        this.styles[level],
        message,
        context ? context : ''
      ].filter(item => item !== '')
    } else {
      // Production: Plain text (for log aggregation services)
      const parts = [`[${level}] ${timestamp} - ${message}`]
      if (context) {
        parts.push(JSON.stringify(context))
      }
      return parts
    }
  }

  /**
   * Internal logging method
   * @private
   */
  _log(level, consoleMethod, message, context) {
    if (!this._shouldLog(level)) {
      return // Silently skip if level is disabled
    }

    const formatted = this._formatMessage(level, message, context)

    if (this.isDevelopment) {
      consoleMethod(...formatted)
    } else {
      consoleMethod(formatted.join(' '))
    }
  }

  /**
   * Debug level logging
   * Use for detailed debugging information (disabled in production)
   *
   * @param {string} message - Log message
   * @param {*} context - Optional context data (object, array, etc.)
   */
  debug(message, context = null) {
    this._log('DEBUG', console.log, message, context)
  }

  /**
   * Info level logging
   * Use for general informational messages (disabled in production)
   *
   * @param {string} message - Log message
   * @param {*} context - Optional context data
   */
  info(message, context = null) {
    this._log('INFO', console.info, message, context)
  }

  /**
   * Warning level logging
   * Use for warning conditions (enabled in all environments)
   *
   * @param {string} message - Log message
   * @param {*} context - Optional context data
   */
  warn(message, context = null) {
    this._log('WARN', console.warn, message, context)
  }

  /**
   * Error level logging
   * Use for error conditions (enabled in all environments)
   * Preserves error stack traces
   *
   * @param {string} message - Log message
   * @param {Error|*} error - Error object or context data
   */
  error(message, error = null) {
    // Special handling for Error objects to preserve stack trace
    if (error instanceof Error) {
      this._log('ERROR', console.error, message, {
        message: error.message,
        stack: error.stack,
        name: error.name
      })
    } else {
      this._log('ERROR', console.error, message, error)
    }
  }

  /**
   * Group logging (for related log entries)
   * Only works in development
   */
  group(label) {
    if (this.isDevelopment && this._shouldLog('DEBUG')) {
      console.group(`🗂️  ${label}`)
    }
  }

  /**
   * End group logging
   */
  groupEnd() {
    if (this.isDevelopment && this._shouldLog('DEBUG')) {
      console.groupEnd()
    }
  }

  /**
   * Table logging (for structured data)
   * Only works in development
   */
  table(data, label = null) {
    if (this.isDevelopment && this._shouldLog('DEBUG')) {
      if (label) {
        console.log(`📊 ${label}`)
      }
      console.table(data)
    }
  }

  /**
   * Time tracking (performance measurement)
   * Only works in development
   */
  time(label) {
    if (this.isDevelopment && this._shouldLog('DEBUG')) {
      console.time(`⏱️  ${label}`)
    }
  }

  /**
   * End time tracking
   */
  timeEnd(label) {
    if (this.isDevelopment && this._shouldLog('DEBUG')) {
      console.timeEnd(`⏱️  ${label}`)
    }
  }

  /**
   * Get current log level name
   */
  getLevel() {
    return Object.keys(this.levels).find(
      key => this.levels[key] === this.currentLevel
    )
  }

  /**
   * Set log level programmatically
   * @param {string} level - Level name (DEBUG, INFO, WARN, ERROR, NONE)
   */
  setLevel(level) {
    const levelUpper = level.toUpperCase()
    if (this.levels[levelUpper] !== undefined) {
      this.currentLevel = this.levels[levelUpper]
      this.info(`Log level changed to ${levelUpper}`)
    } else {
      this.warn(`Invalid log level: ${level}`)
    }
  }
}

// Create singleton instance
const logger = new Logger()

// Expose to window for debugging in development
if (process.env.NODE_ENV === 'development') {
  window.logger = logger
  logger.info('Logger initialized', {
    environment: process.env.NODE_ENV,
    level: logger.getLevel()
  })
}

export default logger
